import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

const DISMISS_KEY = "dentart-pwa-install-dismissed";
const DISMISS_TTL_DAYS = 7;

/** Detects when the browser fires `beforeinstallprompt` (Chrome/Edge/Samsung/Android)
 *  and shows a floating "Instalo aplikacionin" button.
 *  On iOS Safari the event doesn't fire, so we detect standalone + iOS UA
 *  and show an inline hint with the Share → Add to Home Screen instructions. */
export default function InstallPWAButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    // Already installed? Hide.
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) return;

    // Recently dismissed? Hide.
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const at = parseInt(raw, 10) || 0;
        if (Date.now() - at < DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000) return;
      }
    } catch (_e) { /* localStorage unavailable — proceed */ }

    // iOS Safari (no beforeinstallprompt) — show manual hint
    const ua = window.navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    if (isIOS && isSafari) {
      setIosHint(true);
      setVisible(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice.catch(() => ({ outcome: "dismissed" }));
    if (outcome === "accepted") setVisible(false);
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_e) { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  if (iosHint) {
    return (
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-96 z-50 rounded-xl bg-white shadow-xl border border-[#E2DFD8] p-4"
           data-testid="pwa-install-ios">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#2C4C3B] text-[#FDFBF7]">
            <Download size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#1A1A1A]">Instalo në telefonin tënd</div>
            <div className="mt-1 text-xs text-[#5C5C5C] leading-relaxed">
              Kliko <b>Ndaj</b> <span aria-hidden>⎋</span> në Safari, pastaj <b>"Shto te Ekrani Kryesor"</b> për ta hapur si aplikacion.
            </div>
          </div>
          <button onClick={dismiss} aria-label="Mbyll"
                  className="text-[#5C5C5C] hover:text-[#1A1A1A] shrink-0"
                  data-testid="pwa-install-dismiss">
            <X size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:w-80 z-50 rounded-xl bg-white shadow-xl border border-[#E2DFD8] p-4"
         data-testid="pwa-install-prompt">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#2C4C3B] text-[#FDFBF7]">
          <Download size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-[#1A1A1A]">Instalo DentArt</div>
          <div className="mt-0.5 text-xs text-[#5C5C5C]">Hapje më e shpejtë, punon edhe pa internet të plotë.</div>
          <div className="mt-3 flex gap-2">
            <button onClick={install}
                    className="inline-flex items-center gap-1.5 rounded-md bg-[#2C4C3B] text-[#FDFBF7] px-3 py-1.5 text-xs font-medium hover:bg-[#1F3628]"
                    data-testid="pwa-install-button">
              <Download size={12} /> Instalo
            </button>
            <button onClick={dismiss}
                    className="rounded-md px-3 py-1.5 text-xs text-[#5C5C5C] hover:text-[#1A1A1A]"
                    data-testid="pwa-install-later">Më vonë</button>
          </div>
        </div>
        <button onClick={dismiss} aria-label="Mbyll"
                className="text-[#5C5C5C] hover:text-[#1A1A1A] shrink-0"
                data-testid="pwa-install-close">
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
